import type { Exercise, Lesson, PhraseChoice } from '@fluent/types';
import { resolvedPromptEn } from './phraseChoiceText.js';

/**
 * Read-only review model — the de-duplicated teaching content of a lesson,
 * derived from its exercises. Shared by:
 *   • LessonRecap (Feature 2 — recap of a completed lesson)
 *   • LessonPlayer's past-exercise review (Feature 1 — back/forward nav)
 *
 * Only TEACHING content is surfaced. Pure test exercises
 * (translate_typed/tap, listen_type/choose, choose_correct, cloze,
 * phrase_choice) duplicate
 * the phrase_intros, so they're dropped from the recap; their English phrase
 * is tracked for dedup but never rendered on its own.
 */

/** A grammar-tip block. */
export interface ReviewGrammar {
  kind: 'grammar';
  /** Stable key for React lists. */
  key: string;
  titleKa?: string;
  ka: string;
  /** The tip's explainer video (▶ button), when it has one. */
  videoKey?: string;
}

/** A single English↔Georgian phrase row, optionally with audio. */
export interface ReviewPhrase {
  kind: 'phrase';
  key: string;
  en: string;
  ka: string;
  noteKa?: string;
  audioKey?: string;
}

/** One speaker line inside a dialogue block. */
export interface ReviewDialogueLine {
  speaker: string;
  en: string;
  ka: string;
  audioKey?: string;
}

/** A multi-line dialogue block. */
export interface ReviewDialogue {
  kind: 'dialogue';
  key: string;
  noteKa?: string;
  lines: ReviewDialogueLine[];
}

export type ReviewBlock = ReviewGrammar | ReviewPhrase | ReviewDialogue;

/**
 * Plain-text snapshot of a review card — the visible en/ka/note strings
 * joined with newlines. Used by the /review content-review tool as the
 * `original_text` of a filed correction, so the correction stays meaningful
 * even after the content JSON changes.
 */
export function blockSnapshot(block: ReviewBlock): string {
  switch (block.kind) {
    case 'grammar':
      return [block.titleKa, block.ka].filter(Boolean).join('\n');
    case 'phrase':
      return [block.en, block.ka, block.noteKa].filter(Boolean).join('\n');
    case 'dialogue':
      return [
        block.noteKa,
        ...block.lines.map((l) => `${l.speaker}: ${l.en}\n${l.ka}`),
      ]
        .filter(Boolean)
        .join('\n');
  }
}

/** Lowercased, trimmed English phrase — the dedup key for spoken phrases. */
function phraseKey(en: string): string {
  return en.trim().toLowerCase();
}

/**
 * Build the de-duplicated, ordered list of teaching blocks for a whole
 * lesson. Each distinct English phrase appears at most once (first wins,
 * preserving teaching order). Grammar tips and dialogues always pass through.
 */
export function buildLessonReview(lesson: Lesson): ReviewBlock[] {
  const blocks: ReviewBlock[] = [];
  const seenPhrases = new Set<string>();

  for (const ex of lesson.exercises) {
    for (const block of reviewBlocksForExercise(ex)) {
      if (block.kind === 'phrase') {
        const k = phraseKey(block.en);
        if (seenPhrases.has(k)) continue;
        seenPhrases.add(k);
      }
      if (block.kind === 'dialogue') {
        // Mark the dialogue's lines as seen so a later phrase_intro of the
        // same line doesn't double up (and vice-versa).
        for (const line of block.lines) seenPhrases.add(phraseKey(line.en));
      }
      blocks.push(block);
    }
  }

  return blocks;
}

/**
 * Map a single exercise to its teaching block, or `null` for pure test
 * exercises that carry no standalone teaching content. Discriminated union on
 * `exercise.type` — exhaustive, no `any`.
 */
export function reviewBlockForExercise(ex: Exercise): ReviewBlock | null {
  switch (ex.type) {
    case 'grammar_tip':
      return { kind: 'grammar', key: ex.id, titleKa: ex.title_ka, ka: ex.ka, videoKey: ex.video_key };
    case 'phrase_intro':
      return {
        kind: 'phrase',
        key: ex.id,
        en: ex.en,
        ka: ex.ka,
        noteKa: ex.note_ka,
        audioKey: ex.audio_key,
      };
    case 'tap_pairs':
      // tap_pairs teaches several phrases at once — it fans out into one
      // phrase row per pair via `reviewBlocksForExercise`, so the single-block
      // mapper returns null here.
      return null;
    case 'dialogue':
      return {
        kind: 'dialogue',
        key: ex.id,
        noteKa: ex.note_ka,
        lines: ex.lines.map((l) => ({
          speaker: l.speaker,
          en: l.en,
          ka: l.ka,
          audioKey: l.audio_key,
        })),
      };
    case 'translate_typed':
    case 'translate_tap':
    case 'listen_type':
    case 'listen_choose':
    case 'choose_correct':
    case 'cloze':
    case 'phrase_choice':
      // Pure tests — their content duplicates the phrase_intros.
      return null;
  }
}

/**
 * Expand an exercise into one-or-more review blocks. Identical to
 * `reviewBlockForExercise` except `tap_pairs` fans out into one phrase row per
 * pair (each pair teaches a distinct phrase). Used by `buildLessonReview`.
 */
export function reviewBlocksForExercise(ex: Exercise): ReviewBlock[] {
  if (ex.type === 'tap_pairs') {
    return ex.pairs.map((p, i) => ({
      kind: 'phrase' as const,
      key: `${ex.id}-${i}`,
      en: p.en,
      ka: p.ka,
      audioKey: p.audio_key,
    }));
  }
  const block = reviewBlockForExercise(ex);
  return block ? [block] : [];
}

/**
 * Read-only blocks for a SINGLE exercise being reviewed in the player
 * (Feature 1 — back/forward navigation onto an already-answered exercise).
 * Unlike `reviewBlocksForExercise`, pure test exercises are NOT dropped here —
 * the user is reviewing the exercise they answered, so we surface its correct
 * content (the answer and, where present, the Georgian prompt + audio).
 */
export function reviewBlocksForReviewedExercise(ex: Exercise): ReviewBlock[] {
  switch (ex.type) {
    case 'grammar_tip':
    case 'phrase_intro':
    case 'dialogue':
    case 'tap_pairs':
      return reviewBlocksForExercise(ex);
    case 'translate_typed':
      return [
        { kind: 'phrase', key: ex.id, en: ex.answer, ka: ex.prompt, audioKey: ex.audio_key },
      ];
    case 'translate_tap':
      return [
        {
          kind: 'phrase',
          key: ex.id,
          en: ex.answer_tokens.join(' '),
          ka: ex.prompt,
          audioKey: ex.audio_key,
        },
      ];
    case 'listen_type':
      return [
        { kind: 'phrase', key: ex.id, en: ex.answer, ka: ex.translation_ka ?? '', audioKey: ex.audio_key },
      ];
    case 'listen_choose':
      return [
        { kind: 'phrase', key: ex.id, en: ex.answer, ka: ex.translation_ka ?? '', audioKey: ex.audio_key },
      ];
    case 'choose_correct':
      return [
        { kind: 'phrase', key: ex.id, en: ex.answer, ka: ex.prompt, audioKey: ex.audio_key },
      ];
    case 'cloze':
      // Show the resolved sentence (blank filled) + Georgian context.
      return [
        {
          kind: 'phrase',
          key: ex.id,
          en: ex.prompt_en.replace('___', ex.answer),
          ka: ex.prompt_ka,
          audioKey: ex.audio_key,
        },
      ];
    case 'phrase_choice':
      return phraseChoiceReviewBlocks(ex);
  }
}

/**
 * A reviewed phrase_choice: the right content with English on the `en` side
 * and Georgian on the `ka` side whichever way the item asked — a `meaning`
 * item's answer is the Georgian, a `pick_phrase` / `situation` item's prompt
 * is. The item's `why_ka` rides along as the note.
 */
function phraseChoiceReviewBlocks(ex: PhraseChoice): ReviewBlock[] {
  const base = { kind: 'phrase' as const, key: ex.id, noteKa: ex.why_ka };
  switch (ex.variant) {
    case 'meaning':
      return [{ ...base, en: ex.prompt_en ?? '', ka: ex.answer, audioKey: ex.prompt_audio_key ?? ex.audio_key }];
    case 'pick_phrase':
    case 'situation':
      return [{ ...base, en: ex.answer, ka: ex.prompt_ka ?? '', audioKey: ex.audio_key }];
    case 'gap':
    case 'complete':
      // The sentence with the answer in place, and its translation.
      return [{ ...base, en: resolvedPromptEn(ex) ?? ex.answer, ka: ex.after_ka ?? '', audioKey: ex.audio_key }];
    case 'reply':
      // The line that was said, then the right reply to it.
      return [
        { kind: 'phrase', key: `${ex.id}-said`, en: ex.prompt_en ?? '', ka: '', audioKey: ex.prompt_audio_key },
        { ...base, en: ex.answer, ka: ex.after_ka ?? '', audioKey: ex.audio_key },
      ];
    case 'paraphrase':
    case 'odd_one_out':
      return [{ ...base, en: ex.answer, ka: ex.after_ka ?? '', audioKey: ex.audio_key }];
  }
}
